import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChevronRight } from '../../../assets/icons/chevron-right.svg';
import { AppDispatch, RootState } from '../../../store';
import { setProposalStatus, setProposalDeploySchemaId } from '../../../store/aiChatSlice';
import { fetchModelSchemaTemplates } from '../../../store/modelSchemaTemplateSlice';
import {
  deployProcessedModelSchema,
  buildInitialSchemaFields,
  generateProcessedModelSchemaId,
  ProcessedModelDeployError,
  ModelTemplate,
  DeployUser,
} from '../../../store/processedModelDeploy';

import SchemaFieldRenderer from '../../SchemaFieldRenderer';
import ScheduleEditor from '../../Schedule/ScheduleEditor';
import { defaultScheduleConfig, ScheduleConfig } from '../../Schedule/scheduleConfig';
import { usePostHogCapture } from '../../../hooks/usePostHogCapture';
import { useProcessedModelLimit } from '../../../hooks/useProcessedModelLimit';
import { validateFieldValue } from '../../../types/modelSchemas';
import { isOutputFieldKey } from '../../../utils/modelOutputUtils';
import { POSTHOG_EVENTS } from '../../../utils/posthogEvents';
import { PLAN_LABEL_BY_TIER, ProcessedModelLimitCheck } from '../../../api/stripeSubscription';

import Select from '../../ReusableComponents/Select';
// AI-authored payload from a <model_deploy_proposal> block. fieldValues are best-effort pre-fills the user can correct before approving; files and asset links are never AI-supplied.
export interface ModelDeployProposal {
  templateId: string;
  fieldValues?: Record<string, unknown>;
  owner?: string;
}

export interface ModelDeployProposalCardHandle {
  approve: () => Promise<void>;
}

interface ModelDeployProposalCardProps {
  proposal: ModelDeployProposal;
  conversationId: string;
  messageId: string;
  /** Set while a sibling card is deploying under Approve All — locks this card's inputs and buttons. */
  disabled?: boolean;
}

// Primitive field types whose AI value we can safely overlay onto the editor's { value } shape.
const OVERLAYABLE_TYPES = ['string', 'int', 'float', 'enum', 'datetime', 'boolean'];

// Section render order + titles, mirroring the deploy modal's Step 2.
const SECTION_CONFIG: Array<[string, string]> = [
  ['configuration', 'Configuration'],
  ['inputs', 'Data Inputs'],
  ['outputs', 'Outputs'],
  ['output', 'Outputs'],
  ['general', 'General'],
];

// Seed the editable field map from template defaults, then overlay the AI's inferred primitive values.
function seedSchemaFields(
  fieldDefs: Record<string, any>,
  fieldValues?: Record<string, unknown>
): Record<string, any> {
  const seeded: Record<string, any> = buildInitialSchemaFields(fieldDefs);
  if (!fieldValues) return seeded;
  Object.entries(fieldValues).forEach(([key, val]) => {
    const def = fieldDefs[key];
    if (!def || !OVERLAYABLE_TYPES.includes(def.type)) return;

    if (isOutputFieldKey(key, def)) return;
    const existing = seeded[key] && typeof seeded[key] === 'object' ? seeded[key] : {};
    seeded[key] = { ...existing, value: val };
  });
  return seeded;
}

// Group field entries by section and honor each field's admin-defined order (insertion order otherwise).
function groupFieldsBySection(fieldDefs: Record<string, any>): Record<string, Array<[string, any]>> {
  const bySection: Record<string, Array<[string, any]>> = {};
  Object.entries(fieldDefs).forEach(([key, field]) => {
    const section = field.section || 'general';
    if (!bySection[section]) bySection[section] = [];
    bySection[section].push([key, field]);
  });
  Object.keys(bySection).forEach((section) => {
    bySection[section] = bySection[section]
      .map((entry, i) => ({ entry, i }))
      .sort((a, b) => (a.entry[1].order ?? a.i) - (b.entry[1].order ?? b.i))
      .map((x) => x.entry);
  });
  return bySection;
}

// Why the deploy is blocked — "no plan at all" reads differently from "your plan is full".
const limitMessage = (check: ProcessedModelLimitCheck): string =>
  check.requiresPaidPlan
    ? 'Deploying models requires a paid plan. Subscribe to Essential or Pro to deploy this model.'
    : `Your ${PLAN_LABEL_BY_TIER[check.tier] ?? 'current'} plan includes up to ${check.limit} model${
        check.limit !== 1 ? 's' : ''
      } and you already have ${check.currentCount}. Upgrade your plan to deploy more.`;

// Collapsible panel matching the asset-proposal chevron pattern — lets the (potentially long) card break into sections the user can expand/collapse so it doesn't dominate the chat response.
interface CollapsibleSectionProps {
  title: string;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  // Read-only mode (e.g. after deploy): a native <fieldset disabled> cascades to every form control inside — including SchemaFieldRenderer + ScheduleEditor — while the toggle header stays clickable.
  disabled?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, badge, isOpen, onToggle, disabled = false, children }) => (
  <div className="border-t border-eai-border pt-3 first:border-t-0 first:pt-0">
    <button type="button" onClick={onToggle} className="flex items-center gap-1 w-full">
      <ChevronRight
        className={`w-3 h-3 flex-shrink-0 text-eai-ink-faint transition-transform ${isOpen ? 'rotate-90' : ''}`}
      />
      <span className="text-xs font-semibold text-eai-ink-faint uppercase tracking-wide">{title}</span>
      {badge != null && <span className="text-xs text-eai-ink-faint">{badge}</span>}
    </button>
    {isOpen && (
      <fieldset
        disabled={disabled}
        className={`mt-3 space-y-3 min-w-0 border-0 p-0 m-0 ${disabled ? 'opacity-70' : ''}`}
      >
        {children}
      </fieldset>
    )}
  </div>
);

const ModelDeployProposalCard = React.forwardRef<ModelDeployProposalCardHandle, ModelDeployProposalCardProps>(({
  proposal,
  conversationId,
  messageId,
  disabled = false,
}, ref) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();
  const { evaluate: evaluateModelLimit } = useProcessedModelLimit();
  const user = useSelector((state: RootState) => state.user as unknown as DeployUser);
  const status = useSelector(
    (state: RootState) => state.aiChat.proposalStatuses[conversationId]?.[messageId]
  );
  const templates = useSelector(
    (state: RootState) => state.modelSchemaTemplates.templates as Record<string, ModelTemplate>
  );
  const templatesLoading = useSelector(
    (state: RootState) => state.modelSchemaTemplates.loading as string
  );
  // Persisted across remounts so a retry after a mid-deploy failure reuses the same schema id.
  const persistedSchemaId = useSelector(
    (state: RootState) => state.aiChat.proposalDeploySchemaIds[conversationId]?.[messageId]
  );

  // hasOwnProperty guard — templateId is AI-authored, so a key like "constructor" would otherwise resolve off Object.prototype and render a blank card instead of the unavailable message.
  const template = Object.prototype.hasOwnProperty.call(templates, proposal.templateId)
    ? templates[proposal.templateId]
    : undefined;
  const fieldDefs = useMemo<Record<string, any>>(() => {
    try {
      return template?.fieldDefinitions ? JSON.parse(template.fieldDefinitions) : {};
    } catch {
      return {};
    }
  }, [template]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaFields, setSchemaFields] = useState<Record<string, any>>({});
  const [schedule, setSchedule] = useState<ScheduleConfig>(() => defaultScheduleConfig());
  const [ownerType, setOwnerType] = useState<'personal' | 'organization'>(
    proposal.owner === 'organization' ? 'organization' : 'personal'
  );
  const [selectedOrgKey, setSelectedOrgKey] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState('');
  // Collapsed by default so the card stays short; 'details' opens so the model name is visible.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ details: true });
  const toggleSection = (key: string) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const isSettled = status === 'approved' || status === 'declined';
  // Also locked while a sibling card is mid-deploy under Approve All.
  const isLocked = isSettled || disabled;

  // Skip the cap once the card is settled — a deploy that already happened must not flip to a locked state just because it filled the last slot. null means exempt or still loading (fail open).
  const limitCheck = isSettled ? null : evaluateModelLimit();
  const isBlocked = Boolean(limitCheck && !limitCheck.allowed);

  // Report the block once per card, when it first surfaces.
  const hasCapturedLimit = useRef(false);
  useEffect(() => {
    if (!isBlocked || !limitCheck || hasCapturedLimit.current) return;
    hasCapturedLimit.current = true;
    capture(POSTHOG_EVENTS.MODEL_LIMIT_REACHED, {
      tier: limitCheck.tier,
      limit: limitCheck.limit,
      current_count: limitCheck.currentCount,
      source: 'chat_proposal',
      item: 'model',
      templateId: proposal.templateId,
    });
    // limitCheck is recreated each render; isBlocked is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocked]);

  // Sections that currently hold a validation error — force-opened so the error is never hidden.
  const sectionsWithErrors = useMemo(() => {
    const s = new Set<string>();
    if (errors.name) s.add('details');
    if (errors.owner) s.add('owner');
    if (errors.outputs) {
      s.add('outputs');
      s.add('output');
    }
    Object.keys(errors).forEach((k) => {
      if (k.startsWith('schema_')) s.add(fieldDefs[k.slice('schema_'.length)]?.section || 'general');
    });
    return s;
  }, [errors, fieldDefs]);

  // Templates power the card; fetch them if the chat opened without loading the catalog. Guarded to one attempt per card — a templateId with no matching template (deleted, or invented by the AI) leaves `template` undefined after the fetch settles, which would otherwise re-trigger forever.
  const hasRequestedTemplates = useRef(false);
  useEffect(() => {
    if (!template && templatesLoading !== 'pending' && !hasRequestedTemplates.current) {
      hasRequestedTemplates.current = true;
      dispatch(fetchModelSchemaTemplates());
    }
  }, [template, templatesLoading, dispatch]);

  // Once the template resolves, seed the editable form from its defaults + the AI's pre-fills. Seeded exactly once per card: any templates refetch (another card's missing template, Model Garden, the Add Model wizard) rebuilds the dict with new object identities, and re-seeding on that would silently wipe the user's in-progress edits.
  const hasSeededForm = useRef(false);
  useEffect(() => {
    if (!template || hasSeededForm.current) return;
    hasSeededForm.current = true;
    setName(template.name || '');
    setDescription(template.description || '');
    setSchemaFields(seedSchemaFields(fieldDefs, proposal.fieldValues));
    // fieldDefs is derived from template; proposal is stable for a given card instance.
  }, [template, fieldDefs]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchemaFieldChange = (fieldKey: string, value: any) => {
    setSchemaFields((prev) => ({ ...prev, [fieldKey]: value }));
    if (errors[`schema_${fieldKey}`]) {
      setErrors((prev) => ({ ...prev, [`schema_${fieldKey}`]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Name is required';
    if (ownerType === 'organization' && !selectedOrgKey) {
      newErrors.owner = 'Please select an organization';
    }
    Object.entries(fieldDefs).forEach(([key, field]) => {
      const validation = validateFieldValue(schemaFields[key], field);
      if (!validation.isValid) newErrors[`schema_${key}`] = validation.error || 'Invalid value';
    });
    // Require at least one output when the template defines any (matches the modal).
    const outputKeys = Object.keys(fieldDefs).filter((key) => isOutputFieldKey(key, fieldDefs[key]));
    if (outputKeys.length > 0) {
      const anySelected = outputKeys.some((key) => {
        const v = schemaFields[key];
        return v === true || (v && typeof v === 'object' && v.value === true);
      });
      if (!anySelected) newErrors.outputs = 'Select at least one output';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!template || !validate()) return;
    // Re-check at click time — the count can move while the card sits in the transcript.
    const approvalCheck = evaluateModelLimit();
    if (approvalCheck && !approvalCheck.allowed) {
      capture(POSTHOG_EVENTS.MODEL_LIMIT_REACHED, {
        tier: approvalCheck.tier,
        limit: approvalCheck.limit,
        current_count: approvalCheck.currentCount,
        source: 'chat_proposal_approve',
        item: 'model',
        templateId: proposal.templateId,
      });
      setSubmitError(limitMessage(approvalCheck));
      return;
    }
    // Reuse a previously-minted id (persisted in Redux) so a retry after a partial failure — even across a remount — reuses the same schema id instead of orphaning a half-created schema.
    const schemaId = persistedSchemaId || generateProcessedModelSchemaId(name, template.name || '');
    if (!persistedSchemaId) {
      dispatch(setProposalDeploySchemaId({ conversationId, messageId, schemaId }));
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await deployProcessedModelSchema({
        dispatch,
        user,
        template,
        form: {
          name,
          description,
          type: template.name || '',
          typeId: template.id,
          executionUrl: template.executionUrl || '',
          schemaFields,
          // An external forecast model has no processor, so it never gets a run schedule
          schedules: template?.external ? [] : [schedule],
          isActive: true,
          ownerType,
          selectedOrgKey,
          dependency: '',
        },
        hooks: { setStatus: setDeployStatus },
        schemaId,
      });
      dispatch(setProposalStatus({ conversationId, messageId, status: 'approved' }));
      capture(POSTHOG_EVENTS.MODEL_DEPLOY_APPROVED, { templateId: proposal.templateId, ownerType });
    } catch (error) {
      // When the schema record was created but a later step failed, the record now exists — lock the card as approved (a retry would re-create the same id and error) and surface the warning so the user can finish the missing step from the model's edit screen.
      if (error instanceof ProcessedModelDeployError && error.schemaCreated) {
        dispatch(setProposalStatus({ conversationId, messageId, status: 'approved' }));
        capture(POSTHOG_EVENTS.MODEL_DEPLOY_APPROVED, { templateId: proposal.templateId, ownerType, partial: true });
        setSubmitError(error.message);
      } else {
        setSubmitError(
          error instanceof Error ? error.message : 'Failed to deploy model. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    dispatch(setProposalStatus({ conversationId, messageId, status: 'declined' }));
    capture(POSTHOG_EVENTS.MODEL_DEPLOY_DECLINED, { templateId: proposal.templateId });
  };

  // Keep a ref to handleApprove so Approve All always calls the latest version (closing over the current form state) without resetting the imperative handle on every render.
  const handleApproveRef = useRef(handleApprove);
  useEffect(() => { handleApproveRef.current = handleApprove; });
  useImperativeHandle(ref, () => ({ approve: () => handleApproveRef.current() }), []);

  // Template not in the catalog (e.g. deleted/inactive since the AI proposed it).
  if (!template) {
    // Also "loading" before our own fetch fires, so the card doesn't flash "unavailable" first.
    const stillLoading = templatesLoading === 'pending' || !hasRequestedTemplates.current;
    return (
      <div className="eai-panel mt-3 text-eai-body-sm text-eai-ink-subtle">
        {stillLoading
          ? 'Loading model template…'
          : `Model template "${proposal.templateId}" is unavailable, so it can't be deployed here.`}
      </div>
    );
  }

  // Over the tier cap — show why instead of an editor the user can't submit. Decline stays available so the proposal can still be dismissed from the transcript.
  if (isBlocked && limitCheck) {
    const handleUpgrade = () => {
      capture(POSTHOG_EVENTS.MODEL_LIMIT_UPGRADE_CLICKED, {
        tier: limitCheck.tier,
        limit: limitCheck.limit,
        source: 'chat_proposal',
        item: 'model',
      });
      navigate('/home/subscriptions');
    };

    return (
      <div className="eai-panel mt-3 flex flex-col gap-4 overflow-hidden">
        <div>
          <p className="text-xs font-semibold text-eai-ink-faint uppercase tracking-wide">Deploy Model</p>
          <p className="text-sm font-semibold text-eai-ink-soft mt-1">{template.name}</p>
        </div>

        <p className="text-sm text-eai-danger-text">{limitMessage(limitCheck)}</p>

        <div className="border-t border-eai-border pt-4 flex gap-2 justify-end">
          <button
            type="button"
            onClick={handleUpgrade}
            className="eai-btn-cta-toolbar"
          >
            {limitCheck.requiresPaidPlan ? 'View Plans' : 'Upgrade Plan'}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            className="eai-btn-toolbar"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  const fieldsBySection = groupFieldsBySection(fieldDefs);
  const orgEntries: Array<[string, any]> = Object.entries(user?.organizations?.data ?? {});

  return (
    <div className="eai-panel mt-3 flex flex-col gap-4 overflow-hidden">
      <div>
        <p className="text-xs font-semibold text-eai-ink-faint uppercase tracking-wide">Deploy Model</p>
        <p className="text-sm font-semibold text-eai-ink-soft mt-1">{template.name}</p>
        {template.category && <p className="text-xs text-eai-ink-subtle">Category: {template.category}</p>}
      </div>

      <CollapsibleSection
        title="Details"
        isOpen={openSections.details || sectionsWithErrors.has('details')}
        onToggle={() => toggleSection('details')}
        disabled={isLocked}
      >
        <div>
          <label className="block text-sm font-medium text-eai-ink-soft mb-1">Model Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLocked}
            className={`eai-input ${errors.name ? 'border-eai-danger-solid' : ''}`}
            placeholder="Enter model name"
          />
          {errors.name && <p className="mt-1 text-xs text-eai-danger-text">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-eai-ink-soft mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLocked}
            rows={2}
            className="eai-input h-auto"
            placeholder="Describe what this model does"
          />
        </div>
      </CollapsibleSection>

      {/* Config + output fields — reuses the deploy modal's field renderer (files + asset pickers included). */}
      {SECTION_CONFIG.map(([sectionKey, sectionTitle]) => {
        const sectionFields = fieldsBySection[sectionKey];
        if (!sectionFields || sectionFields.length === 0) return null;
        const isOutputSection = sectionKey === 'outputs' || sectionKey === 'output';
        return (
          <CollapsibleSection
            key={sectionKey}
            title={sectionTitle}
            badge={`(${sectionFields.length})`}
            isOpen={openSections[sectionKey] || sectionsWithErrors.has(sectionKey)}
            onToggle={() => toggleSection(sectionKey)}
            disabled={isLocked}
          >
            {isOutputSection && errors.outputs && (
              <p className="text-xs text-eai-danger-text-soft">{errors.outputs}</p>
            )}
            {sectionFields.map(([key, field]) => (
              <SchemaFieldRenderer
                key={key}
                fieldKey={key}
                field={field}
                value={schemaFields[key]}
                onChange={(value: any) => handleSchemaFieldChange(key, value)}
                error={errors[`schema_${key}`]}
                context={{ ownerType, selectedOrgKey }}
              />
            ))}
          </CollapsibleSection>
        );
      })}

      <CollapsibleSection
        title="Owner"
        isOpen={openSections.owner || sectionsWithErrors.has('owner')}
        onToggle={() => toggleSection('owner')}
        disabled={isLocked}
      >
        <Select
          value={ownerType === 'personal' ? 'personal' : selectedOrgKey}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'personal') {
              setOwnerType('personal');
              setSelectedOrgKey('');
            } else {
              setOwnerType('organization');
              setSelectedOrgKey(value);
            }
          }}
          disabled={isLocked}
          className={errors.owner ? 'border-eai-danger focus:border-eai-danger focus:ring-eai-danger' : ''}
        >
          <option value="personal">Personal ({user?.permissionDetails?.name || 'You'})</option>
          {orgEntries.map(([orgKey, orgData]) => (
            <option key={orgKey} value={orgKey}>
              {orgData?.name || orgKey}
            </option>
          ))}
        </Select>
        {errors.owner && <p className="mt-1 text-xs text-eai-danger-text">{errors.owner}</p>}
      </CollapsibleSection>

      <CollapsibleSection
        title="Schedule"
        isOpen={openSections.schedule}
        onToggle={() => toggleSection('schedule')}
        disabled={isLocked}
      >
        <ScheduleEditor value={schedule} onChange={setSchedule} />
      </CollapsibleSection>

      {deployStatus && !submitError && <p className="text-xs text-eai-ink-subtle">{deployStatus}</p>}
      {submitError && <p className="text-xs text-eai-danger-text">{submitError}</p>}

      <div className="border-t border-eai-border pt-4 flex gap-2 justify-end">
        <button
          type="button"
          onClick={handleApprove}
          disabled={isSubmitting || isLocked}
          className={`eai-btn-cta-toolbar ${status === 'approved' ? 'cursor-default opacity-60' : ''}`}
        >
          {isSubmitting && (
            <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          )}
          {status === 'approved' ? '✓ Deployed' : 'Approve & Deploy'}
        </button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={isSubmitting || isLocked}
          className={`eai-btn-toolbar ${status === 'declined' ? 'cursor-default opacity-60' : ''}`}
        >
          {status === 'declined' ? 'Declined' : 'Decline'}
        </button>
      </div>
    </div>
  );
});

ModelDeployProposalCard.displayName = 'ModelDeployProposalCard';

export default ModelDeployProposalCard;
